(ns app.front.ui.inline-editable
  (:require [app.front.ui.i18n :as ui-i18n]
            [app.front.util :as futil]
            [app.i18n.model :as i18n-model]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.icons :as i]
            [core.front.portal :as portal]
            [core.util.util :as util]
            [potpuri.core :as p]
            [reagent.core :as r]))


(defn- buttons
  "A utility function for making the buttons for each of the different field types"
  [button-opts]
  [:div.inline-edit.flex.flex-row.gap-2
   (futil/for-frag [single-button-opts button-opts
                    :let [text  (:text single-button-opts)
                          class (:class-name single-button-opts)
                          opts  (-> single-button-opts
                                    (p/assoc-if :class-name class)
                                    (dissoc :text)
                                    (update :on-click (fn [on-click-fn]
                                                        #(do (.stopPropagation %)
                                                             (on-click-fn %)))))]]
     (cc/button (merge {:kind :tertiary :size :small} opts) text))])


(defn- focus-on-inline-edit?
  "Returns true if the given on-focus or on-blur event has given focus to an inline-edit button or text field.
  Needed because the confirm buttons should disappear when focus is lost on the text field itself,
  but the click doesn't register if the button is removed as a result of the text field losing focus."
  [js-event]
  (when-let [parent-classes (some-> js-event .-relatedTarget .-parentElement .-className)]
    (str/includes? parent-classes "inline-edit")))


(defn- wrap-js-event-handler
  "Wraps the event handler function to take the actual changed value instead of the JS event"
  [on-event-fn]
  #(-> % futil/tval on-event-fn))

(def field-container :div.flex.flex-col.xl:flex-row.gap-2)

(defn text-field
  "A text field with arbitrary buttons hovering on the lower right.
  You will most likely want `text-field-confirm` instead for standard cancel/confirm buttons.

  Props:
  `:input-opts`   map, passed to the `input` component (type is overridden, on-change receives string value)
  `:button-opts`  seq of maps, mapped to `cc/button` components (or `icon-button` if `:text` is nil)

  The button-opts need to only include `:on-click` but can be used to override the default style.
  They also have an additional key `:text` which can be used for button text"
  [{:keys [input-opts button-opts]}]
  [field-container
   [:input.bx--text-input.bx--text__input.bg-white
    (-> input-opts
        (dissoc :default-value)
        (assoc :type "text")
        (update :on-change wrap-js-event-handler))]
   [buttons button-opts]])

(defn button-opts [{:keys [tr disabled on-confirm on-cancel confirm-opts cancel-opts]}]
  [(-> (or cancel-opts
           {:text (tr [:cancel])})
       (assoc :on-click on-cancel))
   (-> (or confirm-opts
           {:text (tr [:save])})
       (assoc :disabled disabled
              :on-click on-confirm
              :kind     :primary))])

(defn text-field-confirm
  "A text field with confirm and cancel buttons hovering on the lower right.
  Also cancels the change when the text field loses focus.

  Props:
  `:tr`            tr
  `:input-opts`    map, passed to the `input` component (type is overridden, on-change receives string value)
  `:allow-blank?`  boolean, whether confirm button should be disabled when field is blank (default false)
  `:on-confirm`    fn, called with the new value when user clicks confirm or presses enter
  `:on-cancel`     fn, called when the user clicks cancel or the field loses focus
  `:confirm-opts`  optional map, ultimately passed to the confirmation `cc/button` (on-click overridden)
  `:cancel-opts`   optional map, ultimately passed to the cancellation `cc/button` (on-click overridden)

  Default values for confirm and cancel buttons are provided.
  See the docstring for `text-field` for more information on the contents of confirm-opts and cancel-opts."
  [{:keys [input-opts allow-blank? on-confirm on-cancel] :as ctx}]
  (r/with-let [*value (r/atom (or (:default-value input-opts) ""))]
    [text-field
     {:input-opts  (merge {:value       @*value
                           :on-blur     #(when-not (focus-on-inline-edit? %)
                                           (on-cancel))
                           :on-click    #(.stopPropagation %)
                           :on-change   #(reset! *value %)
                           :on-key-down #(when (= "Enter" (.-key %))
                                           (on-confirm @*value))}
                          input-opts)
      :button-opts (button-opts (-> ctx
                                    (assoc :on-confirm #(on-confirm @*value)
                                           :disabled (and (not allow-blank?)
                                                          (str/blank? @*value)))))}]))

(defn i18n-text-field-confirm
  "Like `text-field-confirm`, but blur does not cancel so you can keep language selector outside
  the modal"
  [{:keys                             [input-opts allow-blank? on-confirm disabled] :as ctx
    {:keys [on-change default-value]} :input-opts}]
  (r/with-let [*value (r/atom (or default-value {}))]
    [field-container
     [ui-i18n/localized-string-edit
      (merge input-opts
             {:strings    @*value
              :on-change  (fn [val]
                            (reset! *value val)
                            (when on-change
                              (on-change val)))
              :hide-label true})]
     [buttons (button-opts (merge ctx
                                  {:disabled   (or (and (not allow-blank?)
                                                        (i18n-model/blank? @*value))
                                                   (and (some? disabled) (boolean disabled)))
                                   :on-confirm #(on-confirm @*value)}))]]))


(defn select-field
  "A select component with arbitrary buttons hovering on the lower right.
  You will most likely want `select-field-confirm` instead for standard cancel/confirm buttons.

  Props:
  `:select-opts`  map, passed to the `cc/select` component (on-change receives actual value, not js-event)
  `:items`        seq of maps, mapped to `cc/select-item` components
  `:button-opts`  seq of maps, mapped to `cc/button` components (or `icon-button` if `:text` is nil)

  The button-opts need to only include `:on-click` but can be used to override the default style.
  They also have an additional key `:text` which can be used for button text"
  [{:keys [select-opts items button-opts]}]
  [field-container
   (cc/select (update select-opts :on-change wrap-js-event-handler)
              (futil/for-frag [select-item-opts items]
                (cc/select-item select-item-opts)))
   [buttons button-opts]])


(defn select-field-confirm
  "A select component with confirm and cancel buttons hovering on the lower right.
  Also cancels the change when the select loses focus.

  Props:
  `:tr`            tr
  `:select-opts`   map, passed to the `cc/select` component (on-change receives actual value, not js-event)
  `:items`         seq of maps, mapped to `cc/select-item` components
  `:allow-nil?`    boolean, whether confirm button should be disabled when field is unselected (default false)
  `:on-confirm`    fn, called with the new value when user clicks confirm
  `:on-cancel`     fn, called when the user clicks cancel
  `:confirm-opts`  optional map, ultimately passed to the confirmation `cc/button` (on-click overridden)
  `:cancel-opts`   optional map, ultimately passed to the cancellation `cc/button` (on-click overridden)

  Default values for confirm and cancel buttons are provided.
  See the docstring for `select-field` for more information on the contents of confirm-opts and cancel-opts."
  [{:keys [tr select-opts items allow-nil? on-confirm on-cancel confirm-opts cancel-opts]}]
  (r/with-let [*value (r/atom (or (:default-value select-opts) ""))]
    [select-field
     {:select-opts (-> select-opts
                       (merge {:value     @*value
                               :on-change #(reset! *value %)})
                       (dissoc :default-value))
      :items       items
      :button-opts [(-> (or cancel-opts
                            {:icon        i/remove
                             :description (tr [:cancel])})
                        (assoc :on-click on-cancel))
                    (-> (or confirm-opts
                            {:icon        i/check
                             :description (tr [:confirm])})
                        (assoc :disabled (and (not allow-nil?)
                                              (= "" @*value)))
                        (assoc :on-click #(on-confirm @*value)))]}]))


(defn- redirect-focus
  "Moves focus from the group creation popup back to the text field in case the user clicks the popup.
  Normally inline-editable would close if the user clicks anything but the buttons or the text field."
  [js-event]
  (let [target (.-currentTarget js-event)
        get-fn (fn [tag] (some-> target (.getElementsByTagName tag) (.item 0)))]
    (when-not (-> js-event .-target .-nodeName (= "BUTTON"))
      (some-> (or (get-fn "select")
                  (get-fn "input"))
              .focus))))

(def MARGIN 24)


(defn calculate-position
  "Calculate popup position according to anchor element position and window size"
  [popup-el anchor-el]
  (let [anchor-bounds (.getBoundingClientRect anchor-el)
        anchor-width  (.-width anchor-bounds)
        anchor-height (.-height anchor-bounds)
        width         (.-offsetWidth popup-el)
        height        (.-offsetHeight popup-el)
        scroll-top    (.-scrollY js/window)
        scroll-left   (.-scrollX js/window)
        target-top    (+ (.-top anchor-bounds) scroll-top (/ height -2) (/ anchor-height 2))
        target-left   (+ (.-left anchor-bounds) scroll-left (/ width -2) (/ anchor-width 2))
        ; Fit inside viewport:
        vh            (.-innerHeight js/window)
        vw            (.-innerWidth js/window)
        left          (util/clamp target-left (+ scroll-left MARGIN) (- (+ vw scroll-left) width MARGIN))
        top           (util/clamp target-top (+ scroll-top MARGIN) (- (+ vh scroll-top) height MARGIN))]
    {:left (str left "px") :top (str top "px")}))


(defn popup-wrapper
  "Wraps an inline-editable field in a popup div.
  Clicking outside the wrapper closes the popup.

  Props:
  `:tr`      tr
  `:title`   keyword, translation key for the popup header
  `:class`   string, the CSS classes for the wrapper div
  `:style`   map, the CSS style override for the wrapper div
  `:*anchor` optional, reference to an anchor element on top of which the popup should be positioned"
  [{:keys [*anchor]} _]
  (let [*anchor-ref      (r/atom (when *anchor @*anchor))
        *popup-ref       (r/atom nil)
        *popup-pos       (r/atom nil)
        *resize-observer (r/atom nil)
        update-pos       (fn []
                           (when (and @*anchor-ref @*popup-ref)
                             (let [new-pos (calculate-position @*popup-ref @*anchor-ref)]
                               (when (not= @*popup-pos new-pos)
                                 (reset! *popup-pos new-pos)))))]
    (r/create-class
      {:component-did-mount
       (fn [_]
         (let [resize-observer (js/ResizeObserver. update-pos)]
           (reset! *resize-observer resize-observer)
           (some->> @*anchor-ref (.observe resize-observer))
           (some->> @*popup-ref (.observe resize-observer))
           (.addEventListener js/window "scroll" update-pos)
           (.addEventListener js/window "resize" update-pos)
           (update-pos)))
       :componentDidUpdate
       (fn [_]
         (update-pos))
       :component-will-unmount
       (fn [_]
         (.removeEventListener js/window "scroll" update-pos)
         (.removeEventListener js/window "resize" update-pos)
         (some-> @*resize-observer .disconnect))
       :reagent-render
       (fn [{:keys [tr title class style data-cy *anchor]} inline-editable-child]
         [:<>
          (when-not *anchor
            [:div {:ref #(reset! *anchor-ref %)}])
          (portal/render-in-portal
            [:div.inline-edit.min-w-max.p-4.flex.flex-col.bg-blue-100.border-solid.border-2.border-blue-600.rounded.z-10.animation-appear-pop-out
             {:tab-index -1 ; Makes the popup focusable so it doesn't close when clicked
              :on-focus  redirect-focus
              :class     ["z-[10000]"
                          ; Prevent body overflow flickering before position is calculated:
                          (if @*popup-pos
                            "absolute"
                            "fixed top-0 left-0")
                          class]
              :data-cy   data-cy
              :style     (merge style
                                @*popup-pos)
              :ref       #(reset! *popup-ref %)}
             (when title
               [:div.bx--label (tr [title])])
             inline-editable-child])])})))
